import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { ArrowRight, PartyPopper } from "lucide-react";
import {
  ONBOARDING_ROUTES,
  ONBOARDING_STEP_LABELS,
  onboardingResponseSchema,
  type OnboardingStep,
} from "@nh/shared";
import { authFetch } from "@/shared/api/session";
import { Button } from "@/shared/ui/core";
import { Confetti, Stepper } from "@/shared/ui/ui";

const CELEBRATED_KEY = "itb.onboarding.celebrado";

/** A dónde lleva cada píldora pendiente. */
const STEP_LINKS: Record<OnboardingStep, string> = {
  kyc: "/socio/kyc",
  addresses: "/socio/perfil",
  activity: "/paquetes",
};

/**
 * ═══════════════════════════════════════════════════════════════════
 * Los TRES PRIMEROS PASOS, como píldoras de progreso (Anahí, 28-ago).
 *
 *   1 · KYC     2 · Direcciones     3 · Compra o invita a alguien
 *
 * ── Píldoras, no tarjetas ────────────────────────────────────────────
 * La versión anterior eran tres tarjetas con título, explicación y
 * botón: media pantalla para decir tres palabras. Una píldora por paso
 * —morado claro mientras falta, verde con palomita al cumplirse— dice lo
 * mismo de un vistazo y cabe en una línea. Cada píldora pendiente ES el
 * enlace a donde se cumple; la cumplida deja de ser enlace, porque ya no
 * hay nada que ir a hacer.
 *
 * ── El estado lo calcula el servidor ─────────────────────────────────
 * `/api/onboarding/me` mira expediente, direcciones, compras y red, y de
 * paso deja el recordatorio del día si falta algo. Una llamada en vez de
 * cuatro, y el aviso nace aunque nadie mire la campana.
 * ═══════════════════════════════════════════════════════════════════
 */
export function OnboardingSteps() {
  const { t } = useTranslation();
  const onboarding = useQuery({
    queryKey: ["me", "onboarding"],
    queryFn: () => authFetch(ONBOARDING_ROUTES.mine, onboardingResponseSchema, { baseUrl: "" }),
  });

  const [celebrated, setCelebrated] = useState(() => {
    try {
      return window.localStorage.getItem(CELEBRATED_KEY) === "1";
    } catch {
      return false;
    }
  });

  const data = onboarding.data;
  const completed = data?.completed ?? false;

  useEffect(() => {
    if (!completed || celebrated) return;
    try {
      window.localStorage.setItem(CELEBRATED_KEY, "1");
    } catch {
      // Sin almacenamiento (ventana privada) se celebra otra vez. Da igual.
    }
  }, [completed, celebrated]);

  // Mientras carga no se pinta nada: enseñar tres pasos "sin cumplir" a
  // quien los tiene hechos es una mentira de medio segundo, y de las que
  // más molestan.
  if (!data) return null;
  if (completed && celebrated) return null;

  if (completed) {
    return (
      <section className="relative mb-4 overflow-hidden rounded-3xl bg-gradient-to-br from-ds-violet-pop to-ds-indigo-deep p-8 text-center text-white shadow-ds-cta">
        <Confetti />
        <div className="relative">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-white/15">
            <PartyPopper className="h-7 w-7" aria-hidden="true" />
          </span>
          <h2 className="mt-4 text-[clamp(22px,3vw,30px)] font-semibold leading-tight">
            {t("¡Felicidades! Ya eres parte de la familia It's The Best")}
          </h2>
          <p className="mx-auto mt-2 max-w-lg text-[14px] leading-relaxed text-white/85">
            {t("Completaste tus primeros pasos. Tu cuenta está lista para crecer: comparte tu enlace y empieza a construir tu red.")}
          </p>
          <div className="mt-5 flex flex-wrap justify-center gap-2">
            <Button
              as={Link}
              to="/socio/red"
              className="bg-white text-ds-violet-pop hover:bg-white/90"
              rightIcon={<ArrowRight />}
            >
              {t("Ir a mi red")}
            </Button>
            <Button
              variant="ghost"
              className="text-white hover:bg-white/10"
              onClick={() => setCelebrated(true)}
            >
              {t("Listo")}
            </Button>
          </div>
        </div>
      </section>
    );
  }

  return (
    /*
      SIN card (Anahí, 28-ago: "no deben estar dentro de la card"): el
      stepper flota sobre el fondo del panel, entre los números y el
      resto. Una card más aquí solo añadía una caja blanca dentro de otra.
    */
    <section aria-label={t("Tus primeros pasos")} className="mb-5">
      {/*
        Sin rótulo ni contador (Anahí, 28-ago): las píldoras numeradas ya
        dicen que son pasos, y cuántos van se ve en cuántas están verdes.
        El nombre de la sección vive en `aria-label` para quien no ve.
      */}
      <Stepper
        steps={data.steps.map((item, index) => ({
          id: item.step,
          label: `${index + 1}. ${t(ONBOARDING_STEP_LABELS[item.step])}`,
          done: item.done,
          to: STEP_LINKS[item.step],
          ariaLabel: t(item.done ? "{{paso}}: cumplido" : "{{paso}}: pendiente", {
            paso: `${index + 1}. ${t(ONBOARDING_STEP_LABELS[item.step])}`,
          }),
        }))}
      />
    </section>
  );
}
